import { ShipmentStopType } from "@api/graphql/generated/generated-types";

const STOP_TYPE_ORDER: Record<ShipmentStopType, number> = {
  [ShipmentStopType.PickUp]: 0,
  [ShipmentStopType.DropOff]: 1,
  [ShipmentStopType.Return]: 2,
};

export interface ShipmentStopRouteSortable {
  id: string;
  type: ShipmentStopType;
  stopSequenceNumber?: number | null;
  tasks?: readonly { sequenceNumber: number }[] | null;
}

function minTaskSequenceNumber(
  tasks: readonly { sequenceNumber: number }[] | null | undefined,
): number | undefined {
  return tasks?.reduce<number | undefined>(
    (min, t) =>
      min == null || t.sequenceNumber < min ? t.sequenceNumber : min,
    undefined,
  );
}

function compareShipmentStopsByRouteOrder(
  a: ShipmentStopRouteSortable,
  b: ShipmentStopRouteSortable,
): number {
  const aTaskSeq = minTaskSequenceNumber(a.tasks);
  const bTaskSeq = minTaskSequenceNumber(b.tasks);
  const aHasTask = aTaskSeq != null;
  const bHasTask = bTaskSeq != null;

  // if task available: compare sequence numbers.
  if (aHasTask && bHasTask) {
    const d = aTaskSeq - bTaskSeq;
    if (d !== 0) return d;
    return a.id.localeCompare(b.id);
  }

  if (aHasTask !== bHasTask) return aHasTask ? -1 : 1;

  // if order is unassigned, no task is available: compare stop sequence numbers.
  const aStop = a.stopSequenceNumber;
  const bStop = b.stopSequenceNumber;
  if (aStop != null && bStop != null) {
    const d = aStop - bStop;
    if (d !== 0) return d;
    return a.id.localeCompare(b.id);
  }
  if (aStop != null && bStop == null) return -1;
  if (aStop == null && bStop != null) return 1;

  // if no stop sequence numbers: compare stop type.
  const typeDiff = STOP_TYPE_ORDER[a.type] - STOP_TYPE_ORDER[b.type];
  if (typeDiff !== 0) return typeDiff;
  return a.id.localeCompare(b.id);
}

export function sortShipmentStopsByRouteOrder<
  T extends ShipmentStopRouteSortable,
>(stops: readonly T[] | null | undefined): T[] {
  if (!stops?.length) return [];
  return [...stops].sort(compareShipmentStopsByRouteOrder);
}
